import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
} from '@mui/material';
import { AppButton, AppTextField } from '../../../components/design-system';
import { AssignmentReturn as ReturnIcon } from '@mui/icons-material';
import { opsDevicesService } from '../../../services/OpsDevicesService';
import type { DeviceInventoryStatus } from '@cnapse/shared/schemas.js';

const PRE_DEPLOYMENT_STATUSES: DeviceInventoryStatus[] = ['shipped', 'claimed'];

interface ReturnDeviceModalProps {
  open: boolean;
  serialNumber: string;
  inventoryStatus?: DeviceInventoryStatus | string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ReturnDeviceModal({
  open,
  serialNumber,
  inventoryStatus = 'active',
  onClose,
  onSuccess,
}: ReturnDeviceModalProps) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPreDeployment = PRE_DEPLOYMENT_STATUSES.includes(inventoryStatus as DeviceInventoryStatus);

  const handleClose = () => {
    setReason('');
    setConfirmed(false);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('A reason is required.');
      return;
    }
    if (!confirmed) {
      setError('Please confirm the action by checking the box.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await opsDevicesService.returnDevice(serialNumber, reason.trim());
      onSuccess();
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Return failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReturnIcon color="warning" />
        Return Device
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Serial:</strong> {serialNumber}
          </Typography>
          {inventoryStatus && (
            <Typography variant="body2" color="text.secondary">
              <strong>Current status:</strong> {inventoryStatus}
            </Typography>
          )}
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          {isPreDeployment ? (
            <>
              Marks this device as returned before customer deployment (shipment recalled, refused,
              or never claimed). Use when hardware is back in the warehouse but was already shipped
              on an order.
            </>
          ) : (
            <>
              Marks the device as returned from the customer site. IoT gateways are deprovisioned
              automatically when provisioned.
            </>
          )}{' '}
          After QA, restock via <strong>Reset for Redeployment</strong> or retire permanently.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <AppTextField
          label="Reason for return *"
          fullWidth
          multiline
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={
            isPreDeployment
              ? 'e.g., Shipment recalled, refused delivery, wrong units shipped'
              : 'e.g., End of lease, hardware swap, customer offboarding'
          }
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          }
          label={
            isPreDeployment
              ? 'I confirm this device is physically back and should leave the shipped/claimed lifecycle.'
              : 'I confirm this device has been received back from the customer site.'
          }
        />
      </DialogContent>
      <DialogActions>
        <AppButton onClick={handleClose} disabled={submitting}>
          Cancel
        </AppButton>
        <AppButton
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={submitting || !confirmed || !reason.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Return Device
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}
